import * as Location from 'expo-location';
import { Platform } from 'react-native';
import Constants from 'expo-constants';

export type LocationCoordinates = {
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number;
  heading?: number;
  speed?: number;
};

export type LocationPermissionStatus = 'granted' | 'denied' | 'undetermined' | 'restricted';

const PERMISSION_CACHE_TTL_MS = 30_000;
const LOCATION_CACHE_TTL_MS = 15_000;

let cachedPermission: { granted: boolean; recordedAt: number } | null = null;
let permissionRequestInFlight: Promise<boolean> | null = null;
let cachedCurrentLocation: { value: LocationCoordinates; recordedAt: number } | null = null;
let currentLocationInFlight: Promise<LocationCoordinates | null> | null = null;
const SHARED_WATCH_TEARDOWN_DELAY_MS = 4_000;

type WatchSubscriber = {
  callback: (location: LocationCoordinates) => void;
  omitSimulatorFallback: boolean;
};

let nextWatchSubscriberId = 1;
const watchSubscribers = new Map<number, WatchSubscriber>();
let sharedLocationSubscription: Location.LocationSubscription | null = null;
let sharedSimulatorInterval: NodeJS.Timeout | null = null;
let sharedWatchStartInFlight: Promise<void> | null = null;
let sharedWatchTeardownTimer: NodeJS.Timeout | null = null;

const emitLocationToSubscribers = (location: LocationCoordinates) => {
  watchSubscribers.forEach((subscriber) => {
    try {
      subscriber.callback(location);
    } catch (error) {
      if (__DEV__) {
        console.debug('[Location] Subscriber callback failed:', error);
      }
    }
  });
};

const emitSimulatorFallbackToSubscribers = () => {
  const fallback = getDefaultSimulatorLocation();
  watchSubscribers.forEach((subscriber) => {
    if (subscriber.omitSimulatorFallback) return;
    try {
      subscriber.callback(fallback);
    } catch (error) {
      if (__DEV__) {
        console.debug('[Location] Simulator fallback callback failed:', error);
      }
    }
  });
};

const clearSharedSimulatorInterval = () => {
  if (sharedSimulatorInterval) {
    clearInterval(sharedSimulatorInterval);
    sharedSimulatorInterval = null;
  }
};

const hasNonOmitSimulatorSubscribers = () =>
  Array.from(watchSubscribers.values()).some((subscriber) => !subscriber.omitSimulatorFallback);

const ensureSharedSimulatorFallback = (timeIntervalMs: number) => {
  if (!isIOSSimulator()) return;
  if (!hasNonOmitSimulatorSubscribers()) return;
  emitSimulatorFallbackToSubscribers();
  if (sharedSimulatorInterval) return;
  sharedSimulatorInterval = setInterval(() => {
    emitSimulatorFallbackToSubscribers();
  }, timeIntervalMs);
};

const stopSharedWatchNow = () => {
  if (sharedWatchTeardownTimer) {
    clearTimeout(sharedWatchTeardownTimer);
    sharedWatchTeardownTimer = null;
  }
  if (sharedLocationSubscription) {
    try {
      sharedLocationSubscription.remove();
    } catch (error) {
      if (__DEV__) {
        console.debug('[Location] Error stopping shared watch:', error);
      }
    }
    sharedLocationSubscription = null;
  }
  clearSharedSimulatorInterval();
};

const scheduleSharedWatchTeardownIfIdle = () => {
  if (watchSubscribers.size > 0) return;
  if (sharedWatchTeardownTimer) return;
  sharedWatchTeardownTimer = setTimeout(() => {
    sharedWatchTeardownTimer = null;
    if (watchSubscribers.size > 0) return;
    stopSharedWatchNow();
  }, SHARED_WATCH_TEARDOWN_DELAY_MS);
};

const ensureSharedWatchStarted = async (options?: {
  accuracy?: Location.Accuracy;
  timeInterval?: number;
  distanceInterval?: number;
}) => {
  if (sharedLocationSubscription || sharedSimulatorInterval) {
    return;
  }
  if (sharedWatchStartInFlight) {
    await sharedWatchStartInFlight;
    return;
  }

  const accuracy = options?.accuracy ?? Location.Accuracy.Balanced;
  const timeInterval = options?.timeInterval ?? 5000;
  const distanceInterval = options?.distanceInterval ?? 10;

  sharedWatchStartInFlight = (async () => {
    try {
      const hasPermission = await requestLocationPermissions();
      if (!hasPermission) {
        if (isIOSSimulator()) {
          ensureSharedSimulatorFallback(timeInterval);
        } else if (Platform.OS !== 'web') {
          console.warn('[Location] Permission denied, cannot watch location');
        }
        return;
      }

      clearSharedSimulatorInterval();
      sharedLocationSubscription = await Location.watchPositionAsync(
        {
          accuracy,
          timeInterval,
          distanceInterval,
        },
        (location) => {
          emitLocationToSubscribers({
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            accuracy: location.coords.accuracy || undefined,
            altitude: location.coords.altitude || undefined,
            heading: location.coords.heading || undefined,
            speed: location.coords.speed || undefined,
          });
        }
      );
    } catch (error: any) {
      if (isIOSSimulator() && (error?.code === 'E_LOCATION_UNAVAILABLE' || error?.message?.includes('kCLErrorDomain'))) {
        ensureSharedSimulatorFallback(timeInterval);
        return;
      }
      console.error('[Location] Error starting shared location watcher:', error);
    }
  })();

  try {
    await sharedWatchStartInFlight;
  } finally {
    sharedWatchStartInFlight = null;
  }
};

/**
 * Check if running on iOS simulator
 */
const isIOSSimulator = (): boolean => {
  return Platform.OS === 'ios' && !Constants.isDevice;
};

/**
 * Get default location for simulator (Seoul, South Korea)
 * This is a fallback when location services aren't available in simulator
 */
const getDefaultSimulatorLocation = (): LocationCoordinates => {
  return {
    latitude: 37.5665,
    longitude: 126.9780,
    accuracy: 100, // Simulated accuracy
  };
};

/**
 * Request location permissions
 * Returns true if permission is granted, false otherwise
 */
export const requestLocationPermissions = async (): Promise<boolean> => {
  const now = Date.now();
  if (cachedPermission && (now - cachedPermission.recordedAt) < PERMISSION_CACHE_TTL_MS) {
    return cachedPermission.granted;
  }

  if (permissionRequestInFlight) {
    return permissionRequestInFlight;
  }

  permissionRequestInFlight = (async () => {
  try {
    // On web, location permissions work differently and are often denied
    // Suppress warnings for web platform
    if (Platform.OS === 'web') {
      try {
        const { status: foregroundStatus } = await Location.requestForegroundPermissionsAsync();
        const granted = foregroundStatus === 'granted';
        cachedPermission = { granted, recordedAt: Date.now() };
        return granted;
      } catch (error) {
        // Silently fail on web - location permissions are handled by browser
        cachedPermission = { granted: false, recordedAt: Date.now() };
        return false;
      }
    }
    
    // Request foreground permission first
    const { status: foregroundStatus } = await Location.requestForegroundPermissionsAsync();
    
    if (foregroundStatus !== 'granted') {
      console.warn('[Location] Foreground permission denied');
      cachedPermission = { granted: false, recordedAt: Date.now() };
      return false;
    }

    cachedPermission = { granted: true, recordedAt: Date.now() };
    return true;
  } catch (error) {
    console.error('[Location] Error requesting permissions:', error);
    cachedPermission = { granted: false, recordedAt: Date.now() };
    return false;
  }
  })();

  try {
    return await permissionRequestInFlight;
  } finally {
    permissionRequestInFlight = null;
  }
};

/**
 * Check current location permission status
 */
export const getLocationPermissionStatus = async (): Promise<LocationPermissionStatus> => {
  try {
    const { status } = await Location.getForegroundPermissionsAsync();
    return status as LocationPermissionStatus;
  } catch (error) {
    console.error('[Location] Error checking permission status:', error);
    return 'undetermined';
  }
};

/**
 * Location for **live sharing** only: never returns the generic iOS Simulator default (Seoul),
 * so we do not publish a misleading pin to other users. Use a physical device or set a custom
 * location in Simulator (Features → Location).
 */
export const getCurrentLocationForLiveShare = async (): Promise<LocationCoordinates | null> => {
  try {
    const hasPermission = await requestLocationPermissions();
    if (!hasPermission) {
      if (isIOSSimulator()) {
        console.warn(
          '[Location] Live share: permission denied or simulator without GPS. Set a custom location in Simulator or use a device.',
        );
      }
      return null;
    }

    const location = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
      timeInterval: 5000,
      distanceInterval: 10,
    });

    return {
      latitude: location.coords.latitude,
      longitude: location.coords.longitude,
      accuracy: location.coords.accuracy || undefined,
      altitude: location.coords.altitude || undefined,
      heading: location.coords.heading || undefined,
      speed: location.coords.speed || undefined,
    };
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    if (isIOSSimulator() && (err?.code === 'E_LOCATION_UNAVAILABLE' || err?.message?.includes('kCLErrorDomain'))) {
      console.warn(
        '[Location] Live share: no GPS fix on simulator. Use Simulator → Features → Location → Custom, or a real device.',
      );
      return null;
    }
    console.error('[Location] Error getting location for live share:', error);
    return null;
  }
};

/**
 * Get current user location
 * For iOS simulator, returns a default location if location services aren't available
 */
export const getCurrentLocation = async (): Promise<LocationCoordinates | null> => {
  const now = Date.now();
  if (cachedCurrentLocation && (now - cachedCurrentLocation.recordedAt) < LOCATION_CACHE_TTL_MS) {
    return cachedCurrentLocation.value;
  }

  if (currentLocationInFlight) {
    return currentLocationInFlight;
  }

  currentLocationInFlight = (async () => {
  try {
    const hasPermission = await requestLocationPermissions();
    if (!hasPermission) {
      // If on simulator, return default location
      if (isIOSSimulator()) {
        console.warn('[Location] Permission denied on simulator, using default location');
        const fallback = getDefaultSimulatorLocation();
        cachedCurrentLocation = { value: fallback, recordedAt: Date.now() };
        return fallback;
      }
      return null;
    }

    const location = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
      timeInterval: 5000,
      distanceInterval: 10,
    });

    const coords = {
      latitude: location.coords.latitude,
      longitude: location.coords.longitude,
      accuracy: location.coords.accuracy || undefined,
      altitude: location.coords.altitude || undefined,
      heading: location.coords.heading || undefined,
      speed: location.coords.speed || undefined,
    };
    cachedCurrentLocation = { value: coords, recordedAt: Date.now() };
    return coords;
  } catch (error: any) {
    // Handle iOS simulator location errors gracefully
    if (isIOSSimulator() && error?.code === 'E_LOCATION_UNAVAILABLE') {
      console.warn('[Location] Location unavailable on iOS simulator, using default location. To test with real location, use a physical device or set a custom location in Simulator > Features > Location.');
      const fallback = getDefaultSimulatorLocation();
      cachedCurrentLocation = { value: fallback, recordedAt: Date.now() };
      return fallback;
    }
    
    // Check for kCLErrorDomain error 0 (location unavailable)
    if (isIOSSimulator() && error?.message?.includes('kCLErrorDomain')) {
      console.warn('[Location] Location services not available on iOS simulator, using default location. To test with real location, use a physical device or set a custom location in Simulator > Features > Location.');
      const fallback = getDefaultSimulatorLocation();
      cachedCurrentLocation = { value: fallback, recordedAt: Date.now() };
      return fallback;
    }
    
    console.error('[Location] Error getting current location:', error);
    return null;
  }
  })();

  try {
    return await currentLocationInFlight;
  } finally {
    currentLocationInFlight = null;
  }
};

/**
 * Watch user location changes
 * Returns a subscription that can be removed
 * For iOS simulator, uses a default location if location services aren't available
 */
export const watchLocation = (
  callback: (location: LocationCoordinates) => void,
  options?: {
    accuracy?: Location.Accuracy;
    timeInterval?: number;
    distanceInterval?: number;
    /**
     * When true, never synthesize the Seoul simulator default—omit updates instead so live share
     * does not broadcast a fake city to recipients.
     */
    omitSimulatorFallback?: boolean;
  }
): (() => void) => {
  if (sharedWatchTeardownTimer) {
    clearTimeout(sharedWatchTeardownTimer);
    sharedWatchTeardownTimer = null;
  }
  const subscriberId = nextWatchSubscriberId++;
  watchSubscribers.set(subscriberId, {
    callback,
    omitSimulatorFallback: Boolean(options?.omitSimulatorFallback),
  });

  void ensureSharedWatchStarted(options);

  // Return unsubscribe function
  return () => {
    watchSubscribers.delete(subscriberId);
    if (watchSubscribers.size > 0 && sharedLocationSubscription == null && isIOSSimulator()) {
      // If simulator fallback is active, refresh it so omitFallback subscribers don't receive synthetic data.
      clearSharedSimulatorInterval();
      ensureSharedSimulatorFallback(options?.timeInterval ?? 5000);
      return;
    }
    scheduleSharedWatchTeardownIfIdle();
  };
};

/**
 * Calculate distance between two coordinates (in meters)
 * Uses Haversine formula
 */
export const calculateDistance = (
  coord1: LocationCoordinates,
  coord2: LocationCoordinates
): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (coord1.latitude * Math.PI) / 180;
  const φ2 = (coord2.latitude * Math.PI) / 180;
  const Δφ = ((coord2.latitude - coord1.latitude) * Math.PI) / 180;
  const Δλ = ((coord2.longitude - coord1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Check if a location is within a certain radius of another location
 */
export const isWithinRadius = (
  location: LocationCoordinates,
  center: LocationCoordinates,
  radiusMeters: number
): boolean => {
  const distance = calculateDistance(location, center);
  return distance <= radiusMeters;
};

